/**
 * Workplans — admin/supervisor management page.
 *
 * Admin builds recurring workplan templates (daily checklists at a specific
 * site, with attachments and assigned workers). Each calendar day, the
 * backend auto-generates a per-worker instance when the worker hits
 * /api/workplans/today. This page is the template editor.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Trash2, Edit, Calendar as CalendarIcon, MapPin, Paperclip, Link as LinkIcon, FileText, Image as ImageIcon, ExternalLink, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.__BACKEND_URL__}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const RECURRENCE_OPTIONS = [
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Weekdays (Mon–Fri)' },
  { value: 'weekly', label: 'One specific weekday' },
  { value: 'custom', label: 'Specific weekdays' },
  { value: 'none', label: 'One-off (no recurrence)' },
];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const blankPlan = () => ({
  title: '',
  description: '',
  location: '',
  recurrence: 'weekdays',
  weekdays: [],
  weekday: 0,
  assigned_user_ids: [],
  tasks: [],
  attachments: [],
  active: true,
});

export default function Workplans() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankPlan());
  // Send/share dialog state
  const [sendingPlan, setSendingPlan] = useState(null);
  const [sendUserIds, setSendUserIds] = useState([]);
  const [sendMessage, setSendMessage] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendBusy, setSendBusy] = useState(false);

  const isAdminOrSup = user?.role === 'admin' || user?.role === 'supervisor';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, u] = await Promise.all([
        axios.get(`${API}/workplans`, { headers: authHeaders() }),
        axios.get(`${API}/users`, { headers: authHeaders() }),
      ]);
      setPlans(p.data || []);
      setUsers((u.data || []).filter((x) => !x.deleted_at));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load workplans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); /* eslint-disable-line */ }, [load]);

  const openNew = () => {
    setEditing('new');
    setForm(blankPlan());
  };

  const openEdit = (plan) => {
    setEditing(plan.id);
    setForm({
      ...blankPlan(),
      ...plan,
      tasks: plan.tasks || [],
      attachments: plan.attachments || [],
      assigned_user_ids: plan.assigned_user_ids || [],
      weekdays: plan.weekdays || [],
    });
  };

  const close = () => {
    setEditing(null);
    setForm(blankPlan());
  };

  const addTask = () =>
    setForm((f) => ({ ...f, tasks: [...f.tasks, { id: `t-${Date.now()}`, text: '', order: f.tasks.length }] }));

  const updateTask = (idx, patch) =>
    setForm((f) => ({ ...f, tasks: f.tasks.map((t, i) => (i === idx ? { ...t, ...patch } : t)) }));

  const removeTask = (idx) =>
    setForm((f) => ({ ...f, tasks: f.tasks.filter((_, i) => i !== idx) }));

  const toggleAssignedUser = (id) =>
    setForm((f) => ({
      ...f,
      assigned_user_ids: f.assigned_user_ids.includes(id)
        ? f.assigned_user_ids.filter((x) => x !== id)
        : [...f.assigned_user_ids, id],
    }));

  const toggleWeekday = (i) =>
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(i) ? f.weekdays.filter((x) => x !== i) : [...f.weekdays, i],
    }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error('Workplan needs a title');
      return;
    }
    if (form.assigned_user_ids.length === 0) {
      toast.error('Pick at least one worker to assign');
      return;
    }
    try {
      const payload = { ...form, tasks: form.tasks.filter((t) => (t.text || '').trim()) };
      if (editing === 'new') {
        await axios.post(`${API}/workplans`, payload, { headers: authHeaders() });
        toast.success('Workplan created');
      } else {
        await axios.put(`${API}/workplans/${editing}`, payload, { headers: authHeaders() });
        toast.success('Workplan updated');
      }
      close();
      load();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (plan) => {
    if (!window.confirm(`Delete workplan "${plan.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/workplans/${plan.id}`, { headers: authHeaders() });
      toast.success('Deleted');
      load();
    } catch (err) {
      console.error(err);
      toast.error('Delete failed');
    }
  };

  const openSend = (plan) => {
    setSendingPlan(plan);
    setSendUserIds([]);
    setSendMessage('');
    setSendEmail(true);
  };

  const closeSend = () => {
    setSendingPlan(null);
    setSendUserIds([]);
    setSendMessage('');
  };

  // Workers may only send to teammates. Backend enforces this; the picker just
  // hides non-teammates up front to avoid confusing rejections.
  const sendRecipients = (() => {
    if (!sendingPlan) return [];
    if (isAdminOrSup) return users.filter((u) => u.id !== user?.id);
    return users.filter((u) => u.id !== user?.id && u.team_id && u.team_id === user?.team_id);
  })();

  const toggleSendUser = (uid) =>
    setSendUserIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);

  const submitSend = async () => {
    if (!sendingPlan || sendUserIds.length === 0) return;
    setSendBusy(true);
    try {
      const r = await axios.post(
        `${API}/workplans/${sendingPlan.id}/send`,
        { user_ids: sendUserIds, message: sendMessage || null, send_email: sendEmail },
        { headers: authHeaders() },
      );
      const added = r.data?.added || 0;
      const skipped = r.data?.skipped || 0;
      toast.success(
        `Sent to ${added} ${added === 1 ? 'person' : 'people'}${skipped > 0 ? ` (${skipped} already had it)` : ''}.`
      );
      closeSend();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSendBusy(false);
    }
  };

  // ── Attachments ───────────────────────────────────────────────────────
  const [linkInput, setLinkInput] = useState({ name: '', url: '' });
  const addLink = async () => {
    if (!editing || editing === 'new') {
      toast.info('Save the workplan first, then add attachments');
      return;
    }
    if (!linkInput.name || !linkInput.url) return;
    try {
      const r = await axios.post(
        `${API}/workplans/${editing}/attachments`,
        { type: 'link', name: linkInput.name, url: linkInput.url },
        { headers: authHeaders() },
      );
      setForm((f) => ({ ...f, attachments: [...(f.attachments || []), r.data] }));
      setLinkInput({ name: '', url: '' });
    } catch (err) {
      console.error(err);
      toast.error('Could not add link');
    }
  };

  const uploadFile = async (file) => {
    if (!editing || editing === 'new') {
      toast.info('Save the workplan first, then upload files');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      const base64 = dataUrl.split(',')[1];
      const isImg = file.type.startsWith('image/');
      const isPdf = file.type === 'application/pdf';
      const type = isImg ? 'image' : isPdf ? 'pdf' : 'doc';
      try {
        const r = await axios.post(
          `${API}/workplans/${editing}/attachments`,
          { type, name: file.name, mime: file.type, data_base64: base64 },
          { headers: authHeaders() },
        );
        setForm((f) => ({ ...f, attachments: [...(f.attachments || []), r.data] }));
        toast.success(`Uploaded ${file.name}`);
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.detail || 'Upload failed');
      }
    };
    reader.readAsDataURL(file);
  };

  const removeAttachment = async (attId) => {
    if (!editing || editing === 'new') return;
    try {
      await axios.delete(`${API}/workplans/${editing}/attachments/${attId}`, { headers: authHeaders() });
      setForm((f) => ({ ...f, attachments: f.attachments.filter((a) => a.id !== attId) }));
    } catch (err) {
      console.error(err);
      toast.error('Could not remove');
    }
  };

  if (!isAdminOrSup) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold mb-3">Workplans</h1>
        <p className="text-slate-600 dark:text-slate-400">
          Workplans are managed by admins and supervisors. Your assigned plans appear on your Home
          dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="workplans-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarIcon className="w-6 h-6" /> Workplans
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Build recurring workplans for crews who work the same site every day. Each morning, every
            assigned worker sees their personal instance on their Home dashboard.
          </p>
        </div>
        <Button onClick={openNew} data-testid="workplan-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New Workplan
        </Button>
      </div>

      {loading ? (
        <p className="text-slate-500 py-12 text-center">Loading...</p>
      ) : plans.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-slate-500 mb-4">No workplans yet.</p>
          <Button onClick={openNew}>Create your first workplan</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => (
            <Card key={p.id} className="p-4" data-testid={`workplan-card-${p.id}`}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold">{p.title}</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 capitalize">
                  {p.recurrence}
                </span>
              </div>
              {p.location && (
                <p className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  <MapPin className="w-3 h-3" /> {p.location}
                </p>
              )}
              <p className="text-xs text-slate-500 mb-2">
                {(p.assigned_user_ids || []).length} worker(s) • {(p.tasks || []).length} task(s) •{' '}
                {(p.attachments || []).length} attachment(s)
              </p>
              {p.description && <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2 mb-3">{p.description}</p>}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`workplan-edit-${p.id}`}>
                  <Edit className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openSend(p)}
                  data-testid={`workplan-send-${p.id}`}
                  className="text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
                >
                  <Send className="w-3 h-3 mr-1" /> Send / Share
                </Button>
                {user?.role === 'admin' && (
                  <Button size="sm" variant="ghost" onClick={() => remove(p)} className="text-red-500" data-testid={`workplan-delete-${p.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="workplan-dialog-desc">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'New Workplan' : 'Edit Workplan'}</DialogTitle>
          </DialogHeader>
          <p id="workplan-dialog-desc" className="sr-only">
            Create or edit a workplan template. Assign workers, define daily tasks, set recurrence, and attach reference documents or links.
          </p>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="workplan-title" />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Recurrence</Label>
                <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.recurrence === 'weekly' && (
                <div>
                  <Label>Day of week</Label>
                  <Select value={String(form.weekday)} onValueChange={(v) => setForm({ ...form, weekday: parseInt(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {form.recurrence === 'custom' && (
              <div>
                <Label>Repeat on</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {WEEKDAYS.map((d, i) => (
                    <Button key={d} type="button" size="sm" variant={form.weekdays.includes(i) ? 'default' : 'outline'} onClick={() => toggleWeekday(i)}>
                      {d}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>Assign to workers *</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1 mt-1">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center gap-2">
                    <Checkbox checked={form.assigned_user_ids.includes(u.id)} onCheckedChange={() => toggleAssignedUser(u.id)} />
                    <span className="text-sm">{u.name} <span className="text-xs text-slate-500">({u.role})</span></span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Tasks</Label>
                <Button size="sm" variant="outline" onClick={addTask}>
                  <Plus className="w-3 h-3 mr-1" /> Add task
                </Button>
              </div>
              <div className="space-y-1 mt-2">
                {form.tasks.map((t, i) => (
                  <div key={t.id || i} className="flex gap-2 items-center">
                    <Input value={t.text} onChange={(e) => updateTask(i, { text: e.target.value })} placeholder={`Task ${i + 1}`} />
                    <Button size="icon" variant="ghost" onClick={() => removeTask(i)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Attachments (only when editing an existing plan) */}
            {editing !== 'new' && (
              <div className="border-t pt-4">
                <Label className="flex items-center gap-1">
                  <Paperclip className="w-4 h-4" /> Attachments
                </Label>
                <p className="text-xs text-slate-500 mb-2">
                  Upload checklists (PDF), site photos, or paste a link to a training video / SOP.
                </p>
                <div className="space-y-2">
                  {(form.attachments || []).map((a) => (
                    <div key={a.id} className="flex items-center justify-between text-sm bg-slate-50 dark:bg-slate-800 p-2 rounded">
                      <span className="flex items-center gap-2 truncate">
                        {a.type === 'link' && <LinkIcon className="w-4 h-4" />}
                        {a.type === 'pdf' && <FileText className="w-4 h-4" />}
                        {a.type === 'image' && <ImageIcon className="w-4 h-4" />}
                        {a.type === 'doc' && <FileText className="w-4 h-4" />}
                        {a.url ? (
                          <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">{a.name}</a>
                        ) : (
                          <span className="truncate">{a.name}</span>
                        )}
                      </span>
                      <Button size="icon" variant="ghost" onClick={() => removeAttachment(a.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex flex-col md:flex-row gap-2">
                  <input
                    type="file"
                    onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
                    className="text-sm flex-1"
                    data-testid="workplan-file-upload"
                  />
                </div>
                <div className="mt-2 flex flex-col md:flex-row gap-2">
                  <Input placeholder="Link label (e.g. 'SOP video')" value={linkInput.name} onChange={(e) => setLinkInput({ ...linkInput, name: e.target.value })} />
                  <Input placeholder="https://..." value={linkInput.url} onChange={(e) => setLinkInput({ ...linkInput, url: e.target.value })} />
                  <Button size="sm" onClick={addLink} variant="outline" data-testid="workplan-add-link">
                    <ExternalLink className="w-3 h-3 mr-1" /> Add Link
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={save} data-testid="workplan-save-btn">{editing === 'new' ? 'Create' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send / Share dialog */}
      <Dialog open={!!sendingPlan} onOpenChange={(o) => !o && closeSend()}>
        <DialogContent className="max-w-lg" aria-describedby="workplan-send-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-500" />
              Send &ldquo;{sendingPlan?.title}&rdquo;
            </DialogTitle>
          </DialogHeader>
          <p id="workplan-send-desc" className="text-sm text-slate-500">
            Assign this workplan to one or more {isAdminOrSup ? 'people' : 'teammates'}. They&apos;ll get an in-app notification{sendEmail ? ' and an email' : ''}.
          </p>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="mb-2 block">Recipients ({sendUserIds.length})</Label>
              {sendRecipients.length === 0 ? (
                <p className="text-sm text-slate-500 italic">
                  {isAdminOrSup
                    ? 'No other users in your company.'
                    : 'You don\'t have any teammates yet. Ask an admin to add you to a team.'}
                </p>
              ) : (
                <ul
                  className="max-h-56 overflow-y-auto border rounded divide-y divide-slate-100 dark:divide-slate-800"
                  data-testid="workplan-send-recipient-list"
                >
                  {sendRecipients.map((u) => {
                    const checked = sendUserIds.includes(u.id);
                    const already = (sendingPlan?.assigned_user_ids || []).includes(u.id);
                    return (
                      <li key={u.id}>
                        <button
                          type="button"
                          onClick={() => toggleSendUser(u.id)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-900"
                          data-testid={`workplan-send-pick-${u.id}`}
                        >
                          <Checkbox checked={checked} />
                          <span className="flex-1 text-sm">{u.name}</span>
                          <span className="text-xs text-slate-500">{u.role}</span>
                          {already && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                              has it
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div>
              <Label htmlFor="send-msg">Message (optional)</Label>
              <Textarea
                id="send-msg"
                rows={2}
                maxLength={500}
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                placeholder="e.g., Cover this for me while I'm on vacation next week."
                data-testid="workplan-send-message"
              />
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={sendEmail}
                onCheckedChange={(v) => setSendEmail(!!v)}
                data-testid="workplan-send-email-toggle"
              />
              Also email the workplan to recipients
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeSend}>Cancel</Button>
            <Button
              onClick={submitSend}
              disabled={sendBusy || sendUserIds.length === 0}
              data-testid="workplan-send-submit"
            >
              {sendBusy ? 'Sending…' : `Send to ${sendUserIds.length || 0}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
